import {
  UsersOrGroupSelector as SharedUserOrGroupSelector,
  type IUserOrGroupSelectorProps as ISharedUserOrGroupSelectorProps,
  type IPermissionItem as ISharedPermissionItem,
  type GetUsersFn,
  type GetGroupsFn,
} from '@moko/ui';
import { getUsers, getUsersRefValues } from '../../services/userService';
import { getGroups } from '../../services/groupService';

/** Re-export to keep existing portal imports working. */
export type IPermissionItem = ISharedPermissionItem;

export type IUserOrGroupSelectorProps = Omit<
  ISharedUserOrGroupSelectorProps,
  'getUsers' | 'getUsersRefValues' | 'getGroups'
>;

/**
 * Portal `getUsersRefValues` returns `{ id, key, firstName, lastName,
 * email }` — adapt `key` to `username` to match the shared picker shape.
 */
const refValuesAdapter: GetUsersFn = async (
  abortController,
  pageSize,
  pageNo,
  orderBy,
  search
) => {
  const result = await getUsersRefValues(
    abortController,
    pageSize,
    pageNo,
    orderBy,
    search
  );
  return {
    ...result,
    items: result.items.map((u) => ({
      id: u.id,
      username: u.key,
      firstName: u.firstName,
      lastName: u.lastName,
      email: u.email,
    })),
  };
};

const groupsAdapter: GetGroupsFn = async (
  abortController,
  pageSize,
  pageNo,
  orderBy,
  search
) => {
  const result = await getGroups(
    abortController,
    pageSize,
    pageNo,
    orderBy,
    search
  );
  return {
    ...result,
    items: result.items.map((g) => ({ id: g.id, name: g.name })),
  };
};

/**
 * Portal wrapper that injects portal services into the shared
 * UsersOrGroupSelector from `@moko/ui`. All existing portal callsites
 * keep working unchanged.
 */
const UserOrGroupSelector = (props: IUserOrGroupSelectorProps) => (
  <SharedUserOrGroupSelector
    searchThreshold={1}
    {...props}
    getUsers={getUsers as unknown as GetUsersFn}
    getUsersRefValues={refValuesAdapter}
    getGroups={groupsAdapter}
  />
);

export default UserOrGroupSelector;
